import React, { KeyboardEvent, ReactNode, useEffect } from "react";
import Flex from "common/Boxes/Flex";
import styled from "styled-components";
import SubmitButton from "features/onboarding/common/SubmitButton";
import Text from "common/Typography/Text";
import {
  CODE_BOX_HEIGHT,
  onboardingStyles,
} from "@shared/features/onboarding/styles";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import useVerificationCode from "@shared/features/onboarding/useVerificationCode";
import { openMessage } from "utils/misc";
import Link from "common/Typography/Link.tsx";
import { IS_TELEGRAM_WEB_APP, IS_WEB_VIEW } from "common/constants.ts";
import { requestClipboardData } from "services/webview/webViewBridgeUtils.ts";

interface VerificationCodeProps {
  numOfDigits: number;
  title?: string;
  onSubmit: (code: string) => void;
  isLoading: boolean;
  buttonText?: string;
  defaultCode?: string;
  actionButton?: ReactNode;
  additionalItems?: ReactNode;
}

const VerificationCode = ({
  numOfDigits,
  title,
  onSubmit,
  isLoading,
  buttonText,
  defaultCode,
  actionButton,
  additionalItems,
}: VerificationCodeProps) => {
  const {
    onBackPress,
    hiddenInputProps,
    buttonProps,
    boxesData,
    processClipboardCode,
  } = useVerificationCode({
    numOfDigits,
    onSubmit,
    openMessage,
    defaultCode,
    autoFocus: !IS_WEB_VIEW && !IS_TELEGRAM_WEB_APP,
  });

  const isClipboardAvailable =
    IS_TELEGRAM_WEB_APP &&
    !!navigator.clipboard &&
    !!navigator.clipboard.readText;

  const pasteClipboardCode = async (isAutoPaste?: boolean) => {
    if (IS_WEB_VIEW) {
      try {
        const clipboardData = await requestClipboardData();
        await processClipboardCode(clipboardData.clipboardText, isAutoPaste);
      } catch {}
    } else if (!isClipboardAvailable) return;
    else {
      try {
        const clipboardText = await navigator.clipboard.readText();
        await processClipboardCode(clipboardText, isAutoPaste);
      } catch {}
    }
  };

  const checkClipboardForCode = () => {
    try {
      pasteClipboardCode(true).catch(() => {});
    } catch {}
  };

  useEffect(() => {
    checkClipboardForCode();
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkClipboardForCode();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkClipboardForCode]);

  const onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace") {
      onBackPress();
    }
  };

  const verificationCode = (
    <div style={{ position: "relative" }}>
      <Flex $justifyContent="center">
        {boxesData.map((boxData, index) => {
          return (
            <InputBox
              key={index}
              style={boxData.style}
              $isSelected={boxData.isSelected}
              onClick={boxData.onClick}
            >
              <Text $size={"L"}>{boxData.text}</Text>
            </InputBox>
          );
        })}
      </Flex>
      <HiddenInput
        value={hiddenInputProps.value}
        ref={hiddenInputProps.ref}
        onChange={hiddenInputProps.onChange}
        onKeyDown={onKeyPress}
        onBlur={hiddenInputProps.onBlur}
        onFocus={hiddenInputProps.onFocus}
        stringMode={true}
      />
    </div>
  );

  return (
    <CodeWrapper>
      <InputTitle>{title}</InputTitle>
      {verificationCode}
      {(actionButton || isClipboardAvailable) && (
        <Flex style={{ marginTop: 8 }}>
          {actionButton ?? <div />}
          {(isClipboardAvailable || IS_WEB_VIEW) && (
            <Link onClick={() => pasteClipboardCode()}>Paste Code</Link>
          )}
        </Flex>
      )}
      {additionalItems}
      <StyledSubmitButton
        onClick={buttonProps.onClick}
        disabled={buttonProps.disabled}
        loading={isLoading}
      >
        {buttonText || "Verify"}
      </StyledSubmitButton>
    </CodeWrapper>
  );
};

export default VerificationCode;

const CodeWrapper = styled.div`
  width: 100%;
  max-width: 340px;
`;
const InputBox = styled.div<{ $isSelected: boolean }>`
  ${onboardingStyles.verificationCodeBox};
  border-style: solid;
`;
const InputTitle = styled(Text)`
  margin: 32px 0 8px 0;
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
`;
const StyledSubmitButton = styled(SubmitButton)`
  width: 100%;
  margin: 24px 0 12px 0;
`;
const HiddenInput = styled(CustomInputNumber)`
  position: absolute;
  top: 0;
  left: 0;
  height: ${CODE_BOX_HEIGHT}px;
  opacity: 0;
`;
